/**
 * How auto-send works, shown rather than told (user, 13/10/2026): at each
 * draft's planned time the engine runs the same checks a person would —
 * is sending on, is the window open, is there cap left, does the stop-loss
 * hold — and sends (or, Simulated, says it would have); any check that
 * fails holds the draft for a person, a late draft is never fired blind,
 * and a halt stops the rest of the day. One 12 s cycle; reduced motion sees
 * it all at once.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { ArrowRight, Ban, CheckCircle2, Clock, Eye, Gauge, Hand, Power, Send, ShieldAlert, Sunrise } from 'lucide-react';
import type { DropGenAutoSendMode } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

const NODE = 'inline-flex items-center gap-1.5 rounded-md border bg-background px-2 py-1.5 text-xs animate-dg-story-in motion-reduce:animate-none motion-reduce:opacity-100';
const ARROW = 'h-3.5 w-3.5 shrink-0 text-muted-foreground/60';

const Node: FC<{ at: number; className?: string; children: ReactNode }> = ({ at, className, children }) => (
  <span className={cn(NODE, className ?? 'border-border')} style={{ animationDelay: `${at}s` }}>
    {children}
  </span>
);

interface Props {
  mode: DropGenAutoSendMode;
  /** The campaign group (or the container) whose drafts these are. */
  whose: string;
  cap: number | null;
  hasStopLoss: boolean;
}

export const AutoSendStory: FC<Props> = ({ mode, whose, cap, hasStopLoss }) => {
  const shadow = mode === 'shadow';
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-muted/20 p-3" data-testid="autosend-story" style={{ '--dg-story': '12s' } as React.CSSProperties}>
      <div className="flex flex-wrap items-center gap-2">
        <Node at={0}>
          <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />a draft&apos;s planned time comes
        </Node>
        <ArrowRight className={ARROW} />
        <span className="flex flex-wrap items-center gap-1.5">
          <Node at={1} className={mode === 'off' ? 'border-border' : 'border-success/40'}>
            <Power className={cn('h-3.5 w-3.5', mode === 'off' ? 'text-muted-foreground' : 'text-success')} aria-hidden />
            {mode === 'off' ? 'sending is off' : `${whose} is ${shadow ? 'Simulated' : 'Live'}`}
          </Node>
          {mode === 'off' ? null : (
            <>
          <Node at={1.8} className="border-success/40">
            <Sunrise className="h-3.5 w-3.5 text-success" aria-hidden />
            the window is open
          </Node>
          <Node at={2.6} className="border-success/40">
            <Gauge className="h-3.5 w-3.5 text-success" aria-hidden />
            {cap === null ? 'no daily cap' : `under the cap of ${cap.toLocaleString('en-US')}`}
          </Node>
          <Node at={3.4} className={hasStopLoss ? 'border-success/40' : 'border-border'}>
            <ShieldAlert className={cn('h-3.5 w-3.5', hasStopLoss ? 'text-success' : 'text-muted-foreground')} aria-hidden />
            {hasStopLoss ? 'the stop-loss does not hold' : 'no stop-loss set'}
          </Node>
            </>
          )}
        </span>
        <ArrowRight className={ARROW} />
        {mode === 'off' ? (
          <Node at={2} className="border-warning/40">
            <Hand className="h-3.5 w-3.5 text-warning" aria-hidden />
            the draft waits — <b>you</b> send it
          </Node>
        ) : shadow ? (
          <Node at={4.6} className="border-warning/40">
            <Eye className="h-3.5 w-3.5 text-warning" aria-hidden />
            &ldquo;would have sent&rdquo; in the feed — nothing goes out, you still send
          </Node>
        ) : (
          <Node at={4.6} className="border-success/40">
            <Send className="h-3.5 w-3.5 text-success" aria-hidden />
            <b>sent</b> — a real drop, at its time
          </Node>
        )}
      </div>
      {mode !== 'off' ? (
        <div className="flex flex-wrap items-center gap-2">
          <Node at={6.2} className="border-warning/40">
            <Hand className="h-3.5 w-3.5 text-warning" aria-hidden />
            a check fails → <b>held</b>, the draft waits for a person
          </Node>
          <Node at={7.4} className="border-warning/40">
            <Clock className="h-3.5 w-3.5 text-warning" aria-hidden />
            30 min late → never fired blind, it waits for you
          </Node>
          {hasStopLoss ? (
            <Node at={8.6} className="border-destructive/40">
              <Ban className="h-3.5 w-3.5 text-destructive" aria-hidden />
              the stop-loss holds → <b>halted</b> for the rest of today
            </Node>
          ) : null}
          <Node at={hasStopLoss ? 9.8 : 8.6} className="border-destructive/40">
            <CheckCircle2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
            Kill stops everything, country-wide
          </Node>
        </div>
      ) : null}
    </div>
  );
};
